export const CHECKOUT_URL = 'https://picking-en.impultienda.ar/checkout/';

export const images = {
  mockup: 'https://cdn.magicpatterns.com/uploads/jX9qUUqeE1XH7CJMwNy5MW/PICKING-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/dZvszCVQifBgYrPtvjrTpw/JACK_MILLER_-_PICKING.png',
};

export const beforeItems = [
  'You discover picking errors after the order is already wrong.',
  'The same mistakes keep coming back.',
  'You don’t know exactly where the problem starts.',
  'Decisions are based on impressions instead of data.',
  'Re-picks and rework become part of the daily routine.',
  'You have KPIs, but you’re not always sure what actions they should trigger.',
  'Every supervisor manages the operation differently.',
  'Improvements depend too heavily on individual experience.',
];

export const afterItems = [
  'Identify where your picking errors are coming from.',
  'Separate symptoms from root causes.',
  'Use KPIs to monitor operational performance.',
  'Prioritize problems based on frequency and impact.',
  'Standardize supervisor routines.',
  'Implement corrective actions through a 30-day roadmap.',
  'Build repeatable control routines.',
  'Create a consistent process for managing picking performance.',
];

export const pains = [
  { icon: 'repeat', text: 'The same picking errors keep happening even after you’ve talked to your team about them.' },
  { icon: 'timer', text: 'Your operation spends too much time fixing orders that should have been picked correctly the first time.' },
  { icon: 'route', text: 'You have issues with locations, travel paths, picking methods or order preparation, but you’re not sure which problem to tackle first.' },
  { icon: 'chart', text: 'You have operational data and KPIs, but turning those numbers into specific improvement actions is difficult.' },
  { icon: 'refresh', text: 'Re-picks and rework have simply become part of the daily operation.' },
  { icon: 'users', text: 'You feel like your warehouse depends too heavily on the experience of a few key supervisors.' },
] as const;

export const questionChain = [
  'What is going wrong',
  'Why is it happening',
  'How much is it costing',
  'What should we fix first',
  'How do we make sure it doesn’t happen again?',
];

export const volumes = [
  {
    number: 'I',
    title: 'Diagnose your picking before you try to fix it',
    intro: 'Before changing routes, procedures or people, you need to know where the real problem is. I’ll walk you through a practical initial assessment designed to help you identify the biggest operational weaknesses in your picking process.',
    items: ['Initial picking assessment', 'Picking Control Scorecard', 'Critical-point map', 'Error classification system', 'Frequency × impact matrix', 'Problem identification framework'],
  },
  {
    number: 'II',
    title: 'Find the root causes behind repeated picking errors',
    intro: 'A recurring error doesn’t disappear just because you tell someone to “pay more attention.” In this volume, you’ll learn how to separate symptoms from causes and use a practical framework to investigate recurring picking problems.',
    items: ['Root-cause map', 'Diagnostic decision tree', 'Root Cause Matrix', 'Error classification framework', 'Picking incident log', 'Corrective-action prioritization system'],
  },
  {
    number: 'III',
    title: 'Choose the right picking method for your operation',
    intro: 'Not every warehouse should pick the same way. You’ll learn how to evaluate when discrete, batch, zone or wave picking may make sense and what operational variables should be considered before changing your picking method.',
    items: ['Discrete picking', 'Batch picking', 'Zone picking', 'Wave picking', 'Selection criteria', 'Comparison matrix', 'Common implementation mistakes', 'Picking Method Assessment Checklist'],
  },
  {
    number: 'IV',
    title: 'Reduce unnecessary travel, congestion and lost time',
    intro: 'Productivity isn’t simply about asking pickers to move faster. Layout, slotting, product placement and travel paths can create unnecessary work throughout the entire shift. In this volume, you’ll learn how to evaluate these elements using a structured process.',
    items: ['Warehouse layout checklist', 'Slotting review', 'ABC classification framework', 'Travel-path review', 'Congestion assessment', 'Layout opportunity matrix', 'Picking route improvement checklist'],
  },
  {
    number: 'V',
    title: 'Control your picking with the KPIs that actually matter',
    intro: 'Measuring more doesn’t automatically improve an operation. You need to know what to measure, how to calculate it, how often to review it, and what action to take when performance changes.',
    items: ['Picking accuracy', 'Picks per hour', 'Lines per hour', 'Orders per hour', 'Picking error rate', 'Rework / re-pick rate', 'Productivity tracking', 'KPI formulas', 'Measurement frequency', 'KPI interpretation', 'Corrective-action triggers'],
  },
];

export const volumeSix = {
  number: 'VI',
  title: 'Install your picking control system in 30 days',
  intro: 'This is the core of Picking Control 30™. Instead of reading the material, putting it in a folder and never opening it again, you’ll have a structured roadmap for turning the system into an operating routine.',
  weeks: [
    { week: 'Week 1', name: 'Diagnose', text: 'Identify your biggest operational problems.' },
    { week: 'Week 2', name: 'Correct', text: 'Prioritize and address the highest-impact issues.' },
    { week: 'Week 3', name: 'Standardize', text: 'Turn successful improvements into repeatable procedures and routines.' },
    { week: 'Week 4', name: 'Control', text: 'Use KPIs and regular reviews to prevent problems from coming back.' },
  ],
  items: ['30-Day Implementation Calendar', 'Weekly action plan', 'Daily control checklist', 'Weekly review framework', 'Corrective-action tracker', 'Supervisor control meeting template', 'Final Picking Control Scorecard'],
};

export const toolkits = [
  { icon: 'clipboard', title: 'Operational checklists', text: 'Practical checklists for reviewing picking accuracy, routes, slotting, errors, productivity and daily controls.' },
  { icon: 'chart', title: 'Ready-to-use KPI frameworks', text: 'Formulas, measurement frequency, interpretation and recommended action triggers.' },
  { icon: 'search', title: 'Root Cause Matrix', text: 'Move from “we’re having too many errors” to identifying where and why those errors are happening.' },
  { icon: 'gauge', title: 'Problem prioritization system', text: 'Decide which problems deserve attention first based on frequency and operational impact.' },
  { icon: 'calendar', title: '30-day implementation plan', text: 'A practical roadmap for implementing the system without trying to change your entire warehouse overnight.' },
] as const;

export const bonuses = [
  {
    number: 1,
    title: 'Warehouse Error Hunter™',
    value: 37,
    image: 'https://cdn.magicpatterns.com/uploads/12KWrYQR5SbB4JtZ6cowfm/BONUS_1_PICKING-EN.png',
    text: 'A practical error-investigation toolkit designed to help you identify common picking problems and uncover potential causes before jumping into solutions. Includes error categories, diagnostic questions and a root-cause analysis matrix.',
    items: [] as string[],
  },
  {
    number: 2,
    title: '15-Minute Picking Diagnostic™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/3ewtyekfrqB73QwkgKBC5K/BONUS_2_PICKING-EN.png',
    text: 'A rapid assessment tool you can use to evaluate your current picking operation and identify the areas that deserve attention first.',
    items: [] as string[],
  },
  {
    number: 3,
    title: 'Hidden Cost of Picking Errors Calculator',
    value: 27,
    image: 'https://cdn.magicpatterns.com/uploads/cRWaqqnKFeoxRhPPKSfU4g/BONUS_3_PICKING-EN.png',
    text: 'A practical tool for estimating the operational impact of picking errors, re-picks, rework and wasted labor time. Because once you can put a number on a problem, it’s much easier to decide what needs to be fixed first.',
    items: [] as string[],
  },
  {
    number: 4,
    title: 'Supervisor Daily Control™',
    value: 29,
    image: 'https://cdn.magicpatterns.com/uploads/8MQGUjzfgCYyBu82zcVRAi/BONUS_4_PICKING-EN.png',
    text: 'A set of practical daily control sheets for opening, running and closing each shift.',
    items: ['Daily priorities', 'Picking incidents', 'Productivity', 'Error tracking', 'Corrective actions', 'Shift handoff', 'End-of-shift review'],
  },
  {
    number: 5,
    title: '30 Picking Control Checklists',
    value: 25,
    image: 'https://cdn.magicpatterns.com/uploads/hNYxAJh94jeZfz8Z2YZVgx/BONUS_5_PICKING-EN.png',
    text: 'A complete collection of checklists covering different areas of the picking operation, so you don’t have to build every control tool from scratch.',
    items: [] as string[],
  },
];

export const guaranteeItems = [
  'Explore all 6 volumes.',
  'Review the checklists.',
  'Use the diagnostic matrices.',
  'Work through the KPI framework.',
  'Run the initial assessment.',
  'Review the 30-day implementation plan.',
];

// Replace names, locations, quotes and photos with verified customer testimonials only.
export const testimonials = [
  {
    name: '[Real Customer Name]',
    role: 'Warehouse Manager · [State, USA]',
    quote: '[Verified testimonial about using the Picking Control 30™ system.]',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/273d95e6-595e-49f2-9e01-083ce63e3231.jpg',
  },
  {
    name: '[Real Customer Name]',
    role: 'Operations Supervisor · [State, USA]',
    quote: '[Verified testimonial about the checklists, KPIs or diagnostic system.]',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/baa83303-42bc-449b-8dbd-162347830ee6.jpg',
  },
  {
    name: '[Real Customer Name]',
    role: 'Distribution Center Manager · [State, USA]',
    quote: '[Verified testimonial about implementing the 30-day plan.]',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/8fc6026f-2927-4ba8-83f6-3adbd3791921.jpg',
  },
  {
    name: '[Real Customer Name]',
    role: 'Fulfillment Operations Manager · [State, USA]',
    quote: '[Verified testimonial about improving operational control.]',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/bfdd922c-110e-44a3-a037-71b56b71487f.jpg',
  },
  {
    name: '[Real Customer Name]',
    role: 'Warehouse Supervisor · [State, USA]',
    quote: '[Verified testimonial about using the system as part of their daily routine.]',
    photo: 'https://cdn.magicpatterns.com/patterns/generated-images/65cb7c57-4e43-4b8e-8cde-40dd0ad0e273.jpg',
  },
];

export const authorQuestions = [
  'Where are we failing?',
  'Why is it happening?',
  'What should we fix first?',
  'How will we know if we’ve actually improved?',
];

export const faqs = [
  { q: 'How will I receive the material?', a: 'Immediately after your purchase, you’ll receive instructions to access and download the 6 volumes and 5 bonus tools in digital format.' },
  { q: 'Does Picking Control 30™ work for different types of warehouses?', a: 'The system is designed to be adaptable to retail, e-commerce, distribution and other warehouse environments where order picking is part of the operation. Some recommendations should be adapted to the specifics of your facility.' },
  { q: 'Do I need a WMS?', a: 'No. The system can be used as a diagnostic and operational control framework whether you use a WMS or not.' },
  { q: 'Do I need to be a logistics expert?', a: 'No. The material is designed for warehouse managers, operations supervisors, fulfillment leaders and other professionals responsible for warehouse performance.' },
  { q: 'How quickly can I start?', a: 'Immediately. You can begin with the initial picking diagnostic as soon as you receive the material.' },
  { q: 'Are the files downloadable?', a: 'Yes. The materials are delivered digitally so you can access and use them as working tools.' },
  { q: 'Is this a one-time payment?', a: 'Yes. You pay $27 USD once and receive access to the purchased product.' },
  { q: 'What if it’s not what I expected?', a: 'You are protected by a 7-day money-back guarantee. Go through the material and determine whether the system fits your operation.' },
];

export const recentBuyers = [
  { name: 'María González', country: 'Mexico' },
  { name: 'Carlos Ramírez', country: 'Colombia' },
  { name: 'Lucía Fernández', country: 'Spain' },
  { name: 'Javier Morales', country: 'Chile' },
  { name: 'Sofía Herrera', country: 'Argentina' },
  { name: 'Diego Castillo', country: 'Peru' },
  { name: 'Valentina Rojas', country: 'Ecuador' },
  { name: 'Andrés Vargas', country: 'Costa Rica' },
  { name: 'Camila Torres', country: 'Uruguay' },
  { name: 'Miguel Ortega', country: 'Dominican Republic' },
  { name: 'Isabella Mendoza', country: 'Guatemala' },
  { name: 'Alejandro Núñez', country: 'Panama' },
];
